import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { adaptUser, type BackendUser } from "../../utils/user-adapter";
import { SecureStorage } from "../../utils/secure-storage";
import { config } from "../../config/env";

// SSOT: единый источник базового URL API — config.API_BASE_URL (REACT_APP_API_BASE_URL).
// Раньше здесь читался дубль REACT_APP_API_URL с localhost-фолбэком → на проде без этой
// переменной login/register уходили на localhost (ловушка двойной API-переменной).
const API_URL = config.API_BASE_URL;

/**
 * ВАЖНО:
 * 1) Бэкенд NestJS использует глобальный ResponseInterceptor и заворачивает ВСЕ ответы в объект вида { data: ... }.
 * 2) В новой архитектуре фронта для этого написан общий httpClient (src/services/http-client.ts),
 *    который уже распаковывает axios.response.data в доменную ApiResponse<T>.
 * 3) Этот slice — "тонкий" слой вокруг axios и общих auth эндпоинтов, минуя httpClient.
 *
 * Поэтому:
 * - здесь мы ОБЯЗАТЕЛЬНО делаем защитную распаковку (raw?.data ?? raw), чтобы login/register
 *   одинаково работали и с "сырым" backend‑ответом, и с ответом, обёрнутым ResponseInterceptor'ом;
 * - любые новые операции авторизации через Redux должны либо:
 *   а) пользоваться таким же паттерном распаковки, как login/register, ЛИБО
 *   б) использовать authApi/httpClient из src/services/api/auth.api.ts, чтобы не дублировать логику.
 *
 * Несоблюдение этого приводит к тому, что в редьюсер уезжают undefined‑токены и AuthContext/ProtectedRoute
 * расходятся в понимании "авторизован ли пользователь".
 */

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

// Адаптация user из ответа backend (может быть BackendUser или уже адаптированный)
function adaptUserFromResponse(user: BackendUser | User): User {
  // Если уже есть fullName — вернуть как есть
  if ("fullName" in user && user.fullName) {
    return user as User;
  }
  // Иначе — адаптировать
  const adapted = adaptUser(user as BackendUser);
  return {
    id: adapted.id,
    email: adapted.email,
    fullName: adapted.fullName,
    role: adapted.role,
  };
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  loading: boolean;
  error: string | null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterCredentials {
  email: string;
  password: string;
  fullName: string;
}

interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isInitialized: false,
  loading: true,
  error: null,
};

export const initializeAuth = createAsyncThunk("auth/initialize", async () => {
  const [accessToken, refreshToken] = await Promise.all([
    SecureStorage.get("accessToken"),
    SecureStorage.get("refreshToken"),
  ]);
  return { accessToken, refreshToken };
});

export const login = createAsyncThunk<AuthResponse, LoginCredentials>(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);
      // Backend ResponseInterceptor wraps all responses in { data: ... }
      const raw = response.data;
      const data: AuthResponse =
        raw?.data?.accessToken != null ? raw.data : raw;

      await SecureStorage.set("accessToken", data.accessToken);
      await SecureStorage.set("refreshToken", data.refreshToken);

      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const responseData = error.response.data;
        const message =
          responseData?.message ||
          responseData?.data?.message ||
          "Ошибка входа";
        return rejectWithValue(message);
      }
      return rejectWithValue("Произошла ошибка при входе");
    }
  },
);

export const register = createAsyncThunk<AuthResponse, RegisterCredentials>(
  "auth/register",
  async (credentials, { rejectWithValue }) => {
    try {
      // Маппинг fullName → name для backend (RegisterDto ожидает 'name')
      const payload = {
        email: credentials.email,
        password: credentials.password,
        name: credentials.fullName,
      };
      const response = await axios.post(`${API_URL}/auth/register`, payload);
      const raw = response.data;
      const data: AuthResponse =
        raw?.data?.accessToken != null ? raw.data : raw;

      await SecureStorage.set("accessToken", data.accessToken);
      await SecureStorage.set("refreshToken", data.refreshToken);

      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Ошибка регистрации",
        );
      }
      return rejectWithValue("Произошла ошибка при регистрации");
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = await SecureStorage.get("refreshToken");
      if (refreshToken) {
        await axios.post(`${API_URL}/auth/logout`, { refreshToken });
      }

      await SecureStorage.remove("accessToken");
      await SecureStorage.remove("refreshToken");

      return null;
    } catch (error) {
      await SecureStorage.remove("accessToken");
      await SecureStorage.remove("refreshToken");

      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data.message || "Ошибка выхода");
      }
      return rejectWithValue("Произошла ошибка при выходе");
    }
  },
);

export const getProfile = createAsyncThunk(
  "auth/getProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const accessToken = state.auth.accessToken;

      if (!accessToken) {
        return rejectWithValue("Отсутствует токен авторизации");
      }

      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Backend может вернуть user напрямую или в обёртке { user: ... }
      return response.data.user ?? response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Ошибка получения профиля",
        );
      }
      return rejectWithValue("Произошла ошибка при получении данных профиля");
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = !!action.payload.accessToken;
        state.isInitialized = true;
        state.loading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.isInitialized = true;
      })
      // Обработка login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = adaptUserFromResponse(action.payload.user);
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.isInitialized = true;
        state.loading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Обработка register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.user = adaptUserFromResponse(action.payload.user);
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.isInitialized = true;
        state.loading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Обработка logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      .addCase(logout.rejected, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      // Обработка getProfile
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.user = adaptUserFromResponse(action.payload);
        state.isAuthenticated = true;
        state.isInitialized = true;
        state.loading = false;
      })
      .addCase(getProfile.rejected, (state) => {
        state.isInitialized = true;
        state.loading = false;
      });
  },
});

export const { clearError } = authSlice.actions;

export default authSlice.reducer;
